/**
 * Stable domain error codes (§97) with HTTP status and German user-facing message.
 * The API always answers errors in the `ApiErrorResponse` format (§96) – never with stack traces.
 *
 * Public endpoints must not leak internal state: e.g. „Code existiert nicht“ and
 * „Code syntaktisch ungültig“ both map to ACTIVATION_CODE_INVALID.
 */
import { z } from 'zod';

export const ErrorCode = {
  // §97
  ACTIVATION_CODE_INVALID: 'ACTIVATION_CODE_INVALID',
  ACTIVATION_CODE_USED: 'ACTIVATION_CODE_USED',
  ACTIVATION_CODE_DISABLED: 'ACTIVATION_CODE_DISABLED',
  ACTIVATION_ALREADY_PENDING: 'ACTIVATION_ALREADY_PENDING',
  VERIFICATION_TOKEN_INVALID: 'VERIFICATION_TOKEN_INVALID',
  VERIFICATION_TOKEN_EXPIRED: 'VERIFICATION_TOKEN_EXPIRED',
  REGISTRATION_NOT_FOUND: 'REGISTRATION_NOT_FOUND',
  PROVISIONING_FAILED: 'PROVISIONING_FAILED',
  LEARNINGSUITE_UNAVAILABLE: 'LEARNINGSUITE_UNAVAILABLE',
  RATE_LIMITED: 'RATE_LIMITED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  // WS0 additions
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  CSRF_INVALID: 'CSRF_INVALID',
  TURNSTILE_FAILED: 'TURNSTILE_FAILED',
  TURNSTILE_REQUIRED: 'TURNSTILE_REQUIRED',
  ACTIVATION_CODE_EXPIRED: 'ACTIVATION_CODE_EXPIRED',
  BATCH_DISABLED: 'BATCH_DISABLED',
  BATCH_NOT_READY: 'BATCH_NOT_READY',
  VERIFICATION_TOKEN_USED: 'VERIFICATION_TOKEN_USED',
  REGISTRATION_SESSION_INVALID: 'REGISTRATION_SESSION_INVALID',
  RESEND_TOO_EARLY: 'RESEND_TOO_EARLY',
  RESEND_LIMIT_REACHED: 'RESEND_LIMIT_REACHED',
  LOGIN_LINK_INVALID: 'LOGIN_LINK_INVALID',
  LOGIN_LINK_EXPIRED: 'LOGIN_LINK_EXPIRED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  LAST_ACTIVE_ADMIN: 'LAST_ACTIVE_ADMIN',
  COMPANY_ARCHIVED: 'COMPANY_ARCHIVED',
  DOCUMENT_NOT_READY: 'DOCUMENT_NOT_READY',
  DOCUMENT_RENDER_UNAVAILABLE: 'DOCUMENT_RENDER_UNAVAILABLE',
  LEARNINGSUITE_GROUP_NOT_FOUND: 'LEARNINGSUITE_GROUP_NOT_FOUND',
  LEARNINGSUITE_REJECTED: 'LEARNINGSUITE_REJECTED',
  EMAIL_DELIVERY_FAILED: 'EMAIL_DELIVERY_FAILED',
  INVALID_STATE_TRANSITION: 'INVALID_STATE_TRANSITION',
  CONFLICT: 'CONFLICT',
  NOT_FOUND: 'NOT_FOUND',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SIGNATURE_INVALID: 'SIGNATURE_INVALID',
  TIMESTAMP_EXPIRED: 'TIMESTAMP_EXPIRED',
  WEBHOOK_REPLAYED: 'WEBHOOK_REPLAYED',
} as const;
export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];
export const ErrorCodeSchema = z.enum(ErrorCode);

export interface ErrorDefinition {
  readonly httpStatus: number;
  /** German message shown to end users / admins. Must not contain internals. */
  readonly message: string;
  /** Short description for SPEC 16 (when the error occurs). */
  readonly when: string;
}

export const ERROR_DEFINITIONS = {
  ACTIVATION_CODE_INVALID: {
    httpStatus: 404,
    message: 'Dieser Aktivierungscode ist ungültig. Bitte prüfen Sie Ihre Eingabe.',
    when: 'Code syntaktisch falsch, nicht vorhanden oder gehört zu einem noch nicht fertigen Batch.',
  },
  ACTIVATION_CODE_USED: {
    httpStatus: 409,
    message: 'Dieser Aktivierungscode wurde bereits verwendet.',
    when: 'Code ist CLAIMED, PROVISIONING oder REDEEMED und die Anfrage gehört nicht zur eigenen Registrierung.',
  },
  ACTIVATION_CODE_DISABLED: {
    httpStatus: 410,
    message:
      'Dieser Aktivierungscode wurde deaktiviert. Bitte wenden Sie sich an Ihren Ansprechpartner.',
    when: 'Code ist DISABLED.',
  },
  ACTIVATION_ALREADY_PENDING: {
    httpStatus: 409,
    message:
      'Für diesen Aktivierungscode läuft bereits eine Aktivierung. Bitte prüfen Sie Ihr E-Mail-Postfach.',
    when: 'Code ist PENDING_EMAIL und der Browser hat keine Session dieser Registrierung; auch Verlierer eines parallelen Starts.',
  },
  VERIFICATION_TOKEN_INVALID: {
    httpStatus: 400,
    message: 'Dieser Bestätigungslink ist ungültig.',
    when: 'Token unbekannt, ersetzt (E-Mail geändert) oder Registrierung nicht mehr PENDING_EMAIL.',
  },
  VERIFICATION_TOKEN_EXPIRED: {
    httpStatus: 410,
    message: 'Dieser Bestätigungslink ist abgelaufen. Bitte starten Sie die Aktivierung erneut.',
    when: 'Token nach Ablauf (24 h) verwendet.',
  },
  REGISTRATION_NOT_FOUND: {
    httpStatus: 404,
    message: 'Die Registrierung wurde nicht gefunden.',
    when: 'Registrierung existiert nicht (Admin) bzw. gehört nicht zur Session.',
  },
  PROVISIONING_FAILED: {
    httpStatus: 502,
    message: 'Die Einrichtung Ihres Zugangs ist fehlgeschlagen. Wir kümmern uns darum.',
    when: 'Provisionierung endgültig fehlgeschlagen (Admin-Ansicht, Job-Ergebnis).',
  },
  LEARNINGSUITE_UNAVAILABLE: {
    httpStatus: 503,
    message:
      'Die Lernplattform ist vorübergehend nicht erreichbar. Der Vorgang wird automatisch wiederholt.',
    when: 'LearningSuite Timeout, 429, 5xx, Netzwerkfehler (RETRYABLE); auch Gruppenprüfung beim Speichern einer Firma nicht möglich.',
  },
  RATE_LIMITED: {
    httpStatus: 429,
    message: 'Zu viele Anfragen. Bitte warten Sie einen Moment und versuchen Sie es erneut.',
    when: 'IP-, Code- oder Endpoint-Rate-Limit überschritten; Antwort enthält Retry-After.',
  },
  UNAUTHORIZED: {
    httpStatus: 401,
    message: 'Bitte melden Sie sich an.',
    when: 'Admin-Endpoint ohne gültige Session.',
  },
  FORBIDDEN: {
    httpStatus: 403,
    message: 'Sie haben keine Berechtigung für diese Aktion.',
    when: 'Session gültig, aber Admin inaktiv oder Aktion nicht erlaubt.',
  },
  VALIDATION_FAILED: {
    httpStatus: 400,
    message: 'Bitte prüfen Sie Ihre Eingaben.',
    when: 'Zod-Validierung von Params, Query oder Body fehlgeschlagen; `details.fieldErrors` enthält die Felder.',
  },
  CSRF_INVALID: {
    httpStatus: 403,
    message: 'Die Anfrage konnte nicht bestätigt werden. Bitte laden Sie die Seite neu.',
    when: 'Mutierender Request ohne gültigen CSRF-Token oder mit fremdem Origin.',
  },
  TURNSTILE_FAILED: {
    httpStatus: 403,
    message: 'Die Sicherheitsprüfung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    when: 'Turnstile-Siteverify negativ oder Token fehlt.',
  },
  TURNSTILE_REQUIRED: {
    httpStatus: 403,
    message: 'Bitte bestätigen Sie die Sicherheitsprüfung.',
    when: 'Codeprüfung nach zu vielen Fehlversuchen ohne Turnstile-Token.',
  },
  ACTIVATION_CODE_EXPIRED: {
    httpStatus: 410,
    message:
      'Dieser Aktivierungscode ist abgelaufen. Bitte wenden Sie sich an Ihren Ansprechpartner.',
    when: 'Batch-`expires_at` liegt in der Vergangenheit.',
  },
  BATCH_DISABLED: {
    httpStatus: 410,
    message:
      'Dieser Aktivierungscode ist nicht mehr gültig. Bitte wenden Sie sich an Ihren Ansprechpartner.',
    when: 'Batch ist DISABLED (öffentlich) bzw. Admin-Aktion auf deaktiviertem Batch.',
  },
  BATCH_NOT_READY: {
    httpStatus: 409,
    message:
      'Die Codes dieses Batches werden noch erzeugt. Bitte versuchen Sie es in Kürze erneut.',
    when: 'Admin-Aktion (PDF, CSV, Sheet, Deaktivieren) auf Batch im Status GENERATING.',
  },
  VERIFICATION_TOKEN_USED: {
    httpStatus: 409,
    message: 'Dieser Bestätigungslink wurde bereits verwendet.',
    when: 'Token bereits eingelöst (`used_at` gesetzt).',
  },
  REGISTRATION_SESSION_INVALID: {
    httpStatus: 401,
    message: 'Ihre Sitzung ist abgelaufen. Bitte öffnen Sie den Link aus Ihrem Schreiben erneut.',
    when: 'Registration-Session-Cookie fehlt, unbekannt oder abgelaufen.',
  },
  RESEND_TOO_EARLY: {
    httpStatus: 429,
    message: 'Bitte warten Sie einen Moment, bevor Sie die E-Mail erneut anfordern.',
    when: 'Resend innerhalb des Cooldowns (60 s); Antwort enthält Retry-After.',
  },
  RESEND_LIMIT_REACHED: {
    httpStatus: 429,
    message:
      'Die maximale Anzahl an Bestätigungsmails wurde erreicht. Bitte prüfen Sie Ihre E-Mail-Adresse.',
    when: 'Mehr als VERIFICATION_EMAIL_MAX_PER_REGISTRATION Mails für eine Registrierung.',
  },
  LOGIN_LINK_INVALID: {
    httpStatus: 400,
    message: 'Dieser Anmeldelink ist ungültig. Bitte fordern Sie einen neuen Link an.',
    when: 'Magic-Link-Token unbekannt oder bereits verwendet.',
  },
  LOGIN_LINK_EXPIRED: {
    httpStatus: 410,
    message: 'Dieser Anmeldelink ist abgelaufen. Bitte fordern Sie einen neuen Link an.',
    when: 'Magic-Link-Token älter als 15 Minuten.',
  },
  SESSION_EXPIRED: {
    httpStatus: 401,
    message: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
    when: 'Admin-Session abgelaufen (sliding oder absolut) oder widerrufen.',
  },
  LAST_ACTIVE_ADMIN: {
    httpStatus: 409,
    message: 'Der letzte aktive Administrator kann nicht deaktiviert werden.',
    when: 'Deaktivierung des letzten aktiven Admins.',
  },
  COMPANY_ARCHIVED: {
    httpStatus: 409,
    message: 'Diese Firma ist archiviert.',
    when: 'Batch-Erstellung für archivierte Firma.',
  },
  DOCUMENT_NOT_READY: {
    httpStatus: 409,
    message: 'Das Dokument wird noch erstellt. Bitte versuchen Sie es in Kürze erneut.',
    when: 'Download eines Dokuments mit Status PENDING/FAILED.',
  },
  DOCUMENT_RENDER_UNAVAILABLE: {
    httpStatus: 503,
    message:
      'Die PDF-Erstellung ist derzeit ausgelastet. Das Dokument wird später automatisch erstellt.',
    when: 'Browser Run Tageskontingent erschöpft bzw. 429 vom Renderer.',
  },
  LEARNINGSUITE_GROUP_NOT_FOUND: {
    httpStatus: 422,
    message: 'Die LearningSuite-Gruppe wurde nicht gefunden.',
    when: 'GET /groups/{id} liefert 404/500 – bei Provisionierung (PERMANENT) oder beim Speichern einer Firma.',
  },
  LEARNINGSUITE_REJECTED: {
    httpStatus: 422,
    message: 'Die Lernplattform hat die Anfrage abgelehnt.',
    when: 'LearningSuite 400/401/403/404/422 bzw. fachlich abgelehnt (PERMANENT).',
  },
  EMAIL_DELIVERY_FAILED: {
    httpStatus: 502,
    message: 'Die E-Mail konnte nicht versendet werden. Bitte versuchen Sie es später erneut.',
    when: 'E-Mail-Provider lehnt Versand ab.',
  },
  INVALID_STATE_TRANSITION: {
    httpStatus: 409,
    message: 'Diese Aktion ist im aktuellen Status nicht möglich.',
    when: 'Transition nicht in CODE_/REGISTRATION_TRANSITIONS oder Guard verletzt.',
  },
  CONFLICT: {
    httpStatus: 409,
    message: 'Der Datensatz wurde zwischenzeitlich geändert. Bitte laden Sie die Seite neu.',
    when: 'Compare-and-Set verloren (meta.changes !== 1) oder Unique-Constraint verletzt.',
  },
  NOT_FOUND: {
    httpStatus: 404,
    message: 'Der angeforderte Eintrag wurde nicht gefunden.',
    when: 'Entität (Firma, Batch, Code, Job, Dokument, Admin) existiert nicht.',
  },
  PAYLOAD_TOO_LARGE: {
    httpStatus: 413,
    message: 'Die Anfrage ist zu groß.',
    when: 'Request-Body überschreitet das Limit.',
  },
  INTERNAL_ERROR: {
    httpStatus: 500,
    message: 'Ein technischer Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
    when: 'Unerwarteter Fehler; Details nur im Log unter der Request-ID.',
  },
  SIGNATURE_INVALID: {
    httpStatus: 401,
    message: 'Ungültige Signatur.',
    when: 'Webhook-/Callback-Signatur (n8n HMAC, Resend Svix) fehlt oder falsch.',
  },
  TIMESTAMP_EXPIRED: {
    httpStatus: 401,
    message: 'Die Anfrage ist abgelaufen.',
    when: 'Signatur-Timestamp außerhalb der Toleranz (300 s).',
  },
  WEBHOOK_REPLAYED: {
    httpStatus: 409,
    message: 'Diese Anfrage wurde bereits verarbeitet.',
    when: 'Signatur/svix-id bereits im webhook_replay_guard.',
  },
} as const satisfies Record<ErrorCode, ErrorDefinition>;

export function getErrorDefinition(code: ErrorCode): ErrorDefinition {
  return ERROR_DEFINITIONS[code];
}

/** One invalid field of a VALIDATION_FAILED error. `path` uses dot notation (`email`, `items.0.name`). */
export const FieldErrorSchema = z.object({
  path: z.string(),
  message: z.string(),
});
export type FieldError = z.infer<typeof FieldErrorSchema>;

/** §96 – uniform error body. `requestId` is shown to users as „Fehler-ID: …“ (§66). */
export const ApiErrorResponseSchema = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    message: z.string(),
    requestId: z.string(),
    details: z
      .object({
        fieldErrors: z.array(FieldErrorSchema).optional(),
        /** Seconds until the action may be retried (RATE_LIMITED, RESEND_TOO_EARLY). */
        retryAfterSeconds: z.number().int().nonnegative().optional(),
      })
      .optional(),
  }),
});
export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;
